/**
 * BarueriFlash - Push Notifications Service
 * Handles push notification subscription and display
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Initialize the service worker and check notification permission
   */
  async init() {
    if (!this.isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', this.registration);

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  /**
   * Check if push notifications are supported and permission status
   */
  getPermissionStatus() {
    if (!this.isSupported) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Request permission for push notifications
   */
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Subscribe to push notifications
   * @param {string} token - User's JWT token for authentication
   */
  async subscribe(token) {
    if (!this.registration) {
      await this.init();
    }

    if (Notification.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    try {
      // For now, we use a simple subscription without VAPID keys
      // In production, you'd generate VAPID keys and use them here
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        // Note: In production, add applicationServerKey with VAPID public key
      });

      // Send subscription to backend
      const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: this.subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register subscription on server');
      }

      console.log('Push subscription successful:', this.subscription);
      return this.subscription;
    } catch (error) {
      console.error('Failed to subscribe:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   * @param {string} token - User's JWT token for authentication
   */
  async unsubscribe(token) {
    if (!this.subscription) {
      return true;
    }

    try {
      await this.subscription.unsubscribe();

      // Notify backend
      await fetch(`${API_URL}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      this.subscription = null;
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  }

  /**
   * Show a local notification (for testing or fallback)
   */
  async showLocalNotification(title, options = {}) {
    if (!this.registration) {
      await this.init();
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/badge72.png',
      vibrate: [200, 100, 200],
      tag: 'barueriflash-local',
      ...options
    };

    return this.registration.showNotification(title, defaultOptions);
  }

  /**
   * Check if currently subscribed
   */
  async getSubscription() {
    if (!this.registration) {
      await this.init();
    }

    this.subscription = await this.registration.pushManager.getSubscription();
    return this.subscription;
  }
}

// Singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;

// Notification types for the app
export const NotificationTypes = {
  NEW_DELIVERY: 'new_delivery',
  ORDER_STATUS: 'order_status',
  NEW_ORDER: 'new_order',
  DOCUMENTS_APPROVED: 'documents_approved',
  DOCUMENTS_REJECTED: 'documents_rejected',
  PAYMENT_RECEIVED: 'payment_received',
  DELIVERY_COMPLETED: 'delivery_completed'
};

// Helper functions
export const formatNotificationData = (type, data) => {
  const templates = {
    [NotificationTypes.NEW_DELIVERY]: {
      title: '🚚 Nova Entrega Disponível!',
      body: `Entrega para ${data.address || 'endereço próximo'}`,
      data: { type, orderId: data.orderId, url: '/entregador' },
      actions: [
        { action: 'view', title: 'Ver Detalhes' },
        { action: 'accept', title: 'Aceitar' }
      ]
    },
    [NotificationTypes.ORDER_STATUS]: {
      title: '📦 Atualização do Pedido',
      body: getStatusMessage(data.status),
      data: { type, orderId: data.orderId, url: `/cliente/pedido/${data.orderId}` },
      actions: [{ action: 'view', title: 'Ver Pedido' }]
    },
    [NotificationTypes.NEW_ORDER]: {
      title: '🛒 Novo Pedido!',
      body: `Você recebeu um novo pedido de R$ ${data.total?.toFixed(2) || '0.00'}`,
      data: { type, orderId: data.orderId, url: '/lojista' },
      requireInteraction: true
    },
    [NotificationTypes.DOCUMENTS_APPROVED]: {
      title: '✅ Documentos Aprovados!',
      body: 'Seus documentos foram verificados. Você já pode aceitar entregas!',
      data: { type, url: '/entregador' }
    },
    [NotificationTypes.DOCUMENTS_REJECTED]: {
      title: '❌ Documentos Rejeitados',
      body: data.reason || 'Seus documentos precisam ser reenviados.',
      data: { type, url: '/entregador/documentos' }
    },
    [NotificationTypes.PAYMENT_RECEIVED]: {
      title: '💰 Pagamento Confirmado!',
      body: `Pagamento de R$ ${data.amount?.toFixed(2) || '0.00'} recebido`,
      data: { type, orderId: data.orderId }
    },
    [NotificationTypes.DELIVERY_COMPLETED]: {
      title: '🎉 Entrega Concluída!',
      body: 'Sua entrega foi realizada com sucesso. Avalie sua experiência!',
      data: { type, orderId: data.orderId, url: `/cliente/avaliar/${data.orderId}` },
      actions: [{ action: 'view', title: 'Avaliar' }]
    }
  };

  return templates[type] || {
    title: 'BarueriFlash',
    body: 'Você tem uma nova notificação',
    data: { type }
  };
};

const getStatusMessage = (status) => {
  const messages = {
    'confirmado': 'Seu pedido foi confirmado!',
    'preparando': 'O lojista está preparando seu pedido',
    'coleta': 'Entregador a caminho da loja',
    'em_rota': 'Seu pedido está a caminho!',
    'entregue': 'Seu pedido foi entregue!'
  };
  return messages[status] || `Status: ${status}`;
};
